import React, { useState, useRef, useEffect } from 'react';
import {
    Box,
    TextField,
    Popover,
    Typography,
    Button,
    IconButton
} from '@mui/material';
import {
    CalendarToday as CalendarIcon,
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

// Color constants
const LIGHT_BLUE = '#EEF5FF';
const DARK_BLUE = '#4566EB';

// Styled components
const StyledPopover = styled(Popover)<{ singleDate?: boolean }>(({ singleDate }) => ({
    '& .MuiPaper-root': {
        borderRadius: '16px',
        boxShadow: '0px 8px 32px rgba(14, 25, 68, 0.15)',
        overflow: 'hidden',
        width: singleDate ? '400px' : '850px',
    },
}));

const StyledContainer = styled(Box)({
    display: 'flex',
    height: '400px',
});

const StyledSidebar = styled(Box)({
    width: '250px',
    backgroundColor: '#FAFBFC',
    padding: '20px',
    borderRight: '1px solid #E5E7EB',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
});

const StyledPresetButton = styled(Button)<{ selected?: boolean }>(({ selected }) => ({
    width: '100%',
    justifyContent: 'flex-start',
    padding: '12px 16px',
    borderRadius: '8px',
    textTransform: 'none',
    fontWeight: selected ? 600 : 400,
    color: selected ? DARK_BLUE : '#676E8B',
    backgroundColor: selected ? LIGHT_BLUE : 'transparent',
    minHeight: '44px',
    fontSize: '14px',
    whiteSpace: 'nowrap',
    '&:hover': {
        backgroundColor: selected ? LIGHT_BLUE : '#F3F4F6',
    },
}));



const StyledCalendarContainer = styled(Box)({
    flex: 1,
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
});

const StyledCalendarHeader = styled(Box)({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
});



const StyledMonthTitle = styled(Typography)({
    fontWeight: 600,
    color: '#0E1944',
    fontSize: '16px',
    minWidth: '120px',
    textAlign: 'center',
});

const StyledCalendarsContainer = styled(Box)({
    display: 'flex',
    gap: '60px',
    flex: 1,
});

const StyledCalendar = styled(Box)({
    flex: 1,
});

const StyledCalendarGrid = styled(Box)({
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: '8px',
});

const StyledDayHeader = styled(Typography)({
    fontSize: '12px',
    fontWeight: 500,
    color: '#676E8B',
    textAlign: 'center',
    padding: '8px 0',
});

const StyledDayButton = styled(Button)<{
    isSelected?: boolean;
    isInRange?: boolean;
    isStart?: boolean;
    isEnd?: boolean;
    isToday?: boolean;
}>(({ isSelected, isInRange, isToday }) => ({
    minWidth: '32px',
    height: '32px',
    padding: 0,
    borderRadius: '50%',
    fontSize: '14px',
    fontWeight: isToday ? 600 : 400,
    color: isSelected ? 'white' : isToday ? DARK_BLUE : '#0E1944',
    backgroundColor: isSelected ? DARK_BLUE : isInRange ? LIGHT_BLUE : 'transparent',
    border: isToday && !isSelected ? `1px solid ${DARK_BLUE}` : 'none',
    '&:hover': {
        backgroundColor: isSelected ? DARK_BLUE : LIGHT_BLUE,
    },
}));

// Types
interface DateRange {
    start: Date | null;
    end: Date | null;
}

interface DatePickerProps {
    value?: string;
    onChange?: (value: string) => void;
    placeholder?: string;
    label?: string;
    singleDate?: boolean;
}

// Preset options
const presetOptions = [
    { label: 'Today', value: 'today' },
    { label: 'Last 7 days', value: 'last7days' },
    { label: 'Last 30 days', value: 'last30days' },
    { label: 'Last 3 months', value: 'last3months' },
    { label: 'This year', value: 'thisyear' },
    { label: 'Last year', value: 'lastyear' },
    { label: 'All time', value: 'alltime' },
];

// Helper functions
const formatDate = (date: Date): string => {
    return date.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
    });
};

const formatDateRange = (start: Date | null, end: Date | null): string => {
    if (!start && !end) return '';
    if (start && !end) return formatDate(start);
    if (start && end) return `${formatDate(start)} - ${formatDate(end)}`;
    return '';
};

const getMonthName = (date: Date): string => {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

const getDaysInMonth = (date: Date): Date[] => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const days: Date[] = [];
    const currentDate = new Date(startDate);

    for (let i = 0; i < 42; i++) { // 6 weeks * 7 days
        days.push(new Date(currentDate));
        currentDate.setDate(currentDate.getDate() + 1);
    }

    return days;
};

const isSameDay = (date1: Date, date2: Date): boolean => {
    return date1.getDate() === date2.getDate() &&
        date1.getMonth() === date2.getMonth() &&
        date1.getFullYear() === date2.getFullYear();
};

const isInRange = (date: Date, start: Date | null, end: Date | null): boolean => {
    if (!start || !end) return false;
    return date >= start && date <= end;
};

const getPresetRange = (preset: string): DateRange => {
    const today = new Date();
    const start = new Date(today);
    const end = new Date(today);

    switch (preset) {
        case 'alltime':
            return { start: null, end: null };
        case 'today':
            return { start, end };
        case 'last7days':
            start.setDate(today.getDate() - 7);
            return { start, end };
        case 'last30days':
            start.setDate(today.getDate() - 30);
            return { start, end };
        case 'last3months':
            start.setMonth(today.getMonth() - 3);
            return { start, end };
        case 'thisyear':
            start.setMonth(0, 1);
            end.setMonth(11, 31);
            return { start, end };
        case 'lastyear':
            start.setFullYear(today.getFullYear() - 1, 0, 1);
            end.setFullYear(today.getFullYear() - 1, 11, 31);
            return { start, end };
        default:
            return { start: null, end: null };
    }
};

const DatePicker: React.FC<DatePickerProps> = ({
    value = '',
    onChange,
    placeholder = 'MM/DD/YYYY - MM/DD/YYYY',
    label,
    singleDate = false,
}) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [dateRange, setDateRange] = useState<DateRange>({ start: null, end: null });
    // If a value is already supplied (e.g. restored from URL), don't default to
    // 'alltime' — otherwise the TextField would render "All time" instead of value.
    const [selectedPreset, setSelectedPreset] = useState<string>(value ? '' : 'alltime');
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [selectingStart, setSelectingStart] = useState(true);

    // When the parent clears `value` (e.g. "Clear filters"), reset internal
    // state so the TextField stops rendering the previously-picked range.
    useEffect(() => {
        if (!value) {
            setDateRange({ start: null, end: null });
            setSelectedPreset('alltime');
            setSelectingStart(true);
        }
    }, [value]);

    const textFieldRef = useRef<HTMLDivElement>(null);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handlePresetClick = (preset: string) => {
        setSelectedPreset(preset);
        const range = getPresetRange(preset);
        setDateRange(range);
        setSelectingStart(true);

        if (onChange) {
            onChange(formatDateRange(range.start, range.end));
        }
    };

    const handleDayClick = (date: Date) => {
        if (singleDate) {
            setDateRange({ start: date, end: null });
            setSelectedPreset('');
            if (onChange) {
                onChange(formatDate(date));
            }
            handleClose(); // Close immediately for single date
        } else {
            if (selectingStart || (!dateRange.start && !dateRange.end)) {
                setDateRange({ start: date, end: null });
                setSelectingStart(false);
                setSelectedPreset('');
            } else {
                if (dateRange.start && date < dateRange.start) {
                    setDateRange({ start: date, end: dateRange.start });
                } else {
                    setDateRange({ start: dateRange.start, end: date });
                }
                setSelectingStart(true);

                const newRange = dateRange.start && date < dateRange.start
                    ? { start: date, end: dateRange.start }
                    : { start: dateRange.start, end: date };

                if (onChange) {
                    onChange(formatDateRange(newRange.start, newRange.end));
                }
            }
        }
    };

    const navigateMonth = (direction: 'prev' | 'next') => {
        setCurrentMonth(prev => {
            const newMonth = new Date(prev);
            if (direction === 'prev') {
                newMonth.setMonth(prev.getMonth() - 1);
            } else {
                newMonth.setMonth(prev.getMonth() + 1);
            }
            return newMonth;
        });
    };

    const nextMonth = new Date(currentMonth);
    nextMonth.setMonth(currentMonth.getMonth() + 1);

    const renderCalendar = (month: Date) => {
        const days = getDaysInMonth(month);
        const today = new Date();

        return (
            <StyledCalendar>
                <StyledMonthTitle>
                    {getMonthName(month)}
                </StyledMonthTitle>
                <StyledCalendarGrid>
                    {['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'].map(day => (
                        <StyledDayHeader key={day}>{day}</StyledDayHeader>
                    ))}
                    {days.map((day, index) => {
                        const isCurrentMonth = day.getMonth() === month.getMonth();
                        const isToday = isSameDay(day, today);
                        const isStart = dateRange.start && isSameDay(day, dateRange.start);
                        const isEnd = dateRange.end && isSameDay(day, dateRange.end);
                        const isSelected = !!isStart || !!isEnd;
                        const inRange = isInRange(day, dateRange.start, dateRange.end);

                        return (
                            <StyledDayButton
                                key={index}
                                onClick={() => handleDayClick(day)}
                                disabled={!isCurrentMonth}
                                isSelected={isSelected}
                                isInRange={inRange && !isSelected}
                                isStart={!!isStart}
                                isEnd={!!isEnd}
                                isToday={isToday}
                                sx={{
                                    opacity: isCurrentMonth ? 1 : 0.3,
                                }}
                            >
                                {day.getDate()}
                            </StyledDayButton>
                        );
                    })}
                </StyledCalendarGrid>
            </StyledCalendar>
        );
    };

    const open = Boolean(anchorEl);

    return (
        <>
            <TextField
                ref={textFieldRef}
                label={label}
                placeholder={placeholder}
                variant="outlined"
                size="small"
                fullWidth
                value={
                    selectedPreset === 'alltime'
                        ? 'All time'
                        : value || (singleDate ? (dateRange.start ? formatDate(dateRange.start) : '') : formatDateRange(dateRange.start, dateRange.end))
                }
                onClick={handleClick}
                InputProps={{
                    readOnly: true,
                    endAdornment: (
                        <CalendarIcon sx={{ color: '#676E8B', fontSize: 20, cursor: 'pointer' }} />
                    ),
                }}
                sx={{
                    '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        cursor: 'pointer',
                    }
                }}
            />

            <StyledPopover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                singleDate={singleDate}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
            >
                <StyledContainer>
                    {!singleDate && (
                        <StyledSidebar>
                            {presetOptions.map((option) => (
                                <StyledPresetButton
                                    key={option.value}
                                    selected={selectedPreset === option.value}
                                    onClick={() => handlePresetClick(option.value)}
                                >
                                    {option.label}
                                </StyledPresetButton>
                            ))}
                        </StyledSidebar>
                    )}

                    <StyledCalendarContainer>
                        <StyledCalendarHeader>
                            <IconButton onClick={() => navigateMonth('prev')} size="small">
                                <ChevronLeftIcon />
                            </IconButton>

                            <Box sx={{ flex: 1 }} />

                            <IconButton onClick={() => navigateMonth('next')} size="small">
                                <ChevronRightIcon />
                            </IconButton>
                        </StyledCalendarHeader>

                        <StyledCalendarsContainer>
                            {renderCalendar(currentMonth)}
                            {!singleDate && renderCalendar(nextMonth)}
                        </StyledCalendarsContainer>
                    </StyledCalendarContainer>
                </StyledContainer>
            </StyledPopover>
        </>
    );
};

export default DatePicker;